// Notification Persistence Service
// Handles persistence of socket subscriptions and connection state across page refreshes

export interface PersistedSubscription {
  runId: string;
  timestamp: number;
  dealerId?: string;
}

export interface ConnectionState {
  connected: boolean;
  lastConnected: number;
  reconnectAttempts: number;
}

class NotificationPersistenceService {
  private static instance: NotificationPersistenceService;
  private readonly STORAGE_KEYS = {
    SUBSCRIPTIONS: 'runLogs_subscriptions',
    CONNECTION_STATE: 'runLogs_connectionState',
    LAST_ACTIVITY: 'runLogs_lastActivity',
    SETTINGS: 'runLogs_settings'
  };

  private constructor() {}

  static getInstance(): NotificationPersistenceService {
    if (!NotificationPersistenceService.instance) {
      NotificationPersistenceService.instance = new NotificationPersistenceService();
    }
    return NotificationPersistenceService.instance;
  }

  // Subscription Management
  saveSubscriptions(subscriptions: string[], dealerId?: string): void {
    try {
      const persistedSubscriptions: PersistedSubscription[] = subscriptions.map(runId => ({
        runId,
        timestamp: Date.now(),
        dealerId
      }));

      localStorage.setItem(this.STORAGE_KEYS.SUBSCRIPTIONS, JSON.stringify(persistedSubscriptions));
    } catch (error) {
    }
  }

  getSubscriptions(): PersistedSubscription[] {
    try {
      const saved = localStorage.getItem(this.STORAGE_KEYS.SUBSCRIPTIONS);
      if (!saved) return [];

      const subscriptions = JSON.parse(saved) as PersistedSubscription[];
      
      // Filter out old subscriptions (older than 24 hours)
      const twentyFourHoursAgo = Date.now() - (24 * 60 * 60 * 1000);
      const validSubscriptions = subscriptions.filter(sub => sub.timestamp > twentyFourHoursAgo);
      
      // Update storage with filtered subscriptions
      if (validSubscriptions.length !== subscriptions.length) {
        this.saveSubscriptions(validSubscriptions.map(sub => sub.runId), validSubscriptions[0]?.dealerId);
      }

      return validSubscriptions;
    } catch (error) {
      return [];
    }
  }

  addSubscription(runId: string, dealerId?: string): void {
    try {
      const existing = this.getSubscriptions();
      const exists = existing.some(sub => sub.runId === runId);
      
      if (!exists) {
        const newSubscription: PersistedSubscription = {
          runId,
          timestamp: Date.now(),
          dealerId
        };
        
        const updated = [...existing, newSubscription];
        localStorage.setItem(this.STORAGE_KEYS.SUBSCRIPTIONS, JSON.stringify(updated));
      }
    } catch (error) {
    }
  }

  removeSubscription(runId: string): void {
    try {
      const existing = this.getSubscriptions();
      const filtered = existing.filter(sub => sub.runId !== runId);
      
      if (filtered.length !== existing.length) {
        localStorage.setItem(this.STORAGE_KEYS.SUBSCRIPTIONS, JSON.stringify(filtered));
      }
    } catch (error) {
    }
  }

  clearAllSubscriptions(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEYS.SUBSCRIPTIONS);
    } catch (error) {
    }
  }

  // Connection State Management
  saveConnectionState(connected: boolean, reconnectAttempts: number = 0): void {
    try {
      const connectionState: ConnectionState = {
        connected,
        lastConnected: connected ? Date.now() : 0,
        reconnectAttempts
      };

      localStorage.setItem(this.STORAGE_KEYS.CONNECTION_STATE, JSON.stringify(connectionState));
    } catch (error) {
    }
  }

  getConnectionState(): ConnectionState {
    try {
      const saved = localStorage.getItem(this.STORAGE_KEYS.CONNECTION_STATE);
      if (!saved) {
        return {
          connected: false,
          lastConnected: 0,
          reconnectAttempts: 0
        };
      }

      return JSON.parse(saved) as ConnectionState;
    } catch (error) {
      return {
        connected: false,
        lastConnected: 0,
        reconnectAttempts: 0
      };
    }
  }

  // Activity Tracking
  updateLastActivity(): void {
    try {
      localStorage.setItem(this.STORAGE_KEYS.LAST_ACTIVITY, Date.now().toString());
    } catch (error) {
    }
  }

  getLastActivity(): number {
    try {
      const saved = localStorage.getItem(this.STORAGE_KEYS.LAST_ACTIVITY);
      return saved ? parseInt(saved, 10) : 0;
    } catch (error) {
      return 0;
    }
  }

  // Settings Management
  saveSettings(settings: Record<string, any>): void {
    try {
      localStorage.setItem(this.STORAGE_KEYS.SETTINGS, JSON.stringify(settings));
    } catch (error) {
    }
  }

  getSettings(): Record<string, any> {
    try {
      const saved = localStorage.getItem(this.STORAGE_KEYS.SETTINGS);
      return saved ? JSON.parse(saved) : {};
    } catch (error) {
      return {};
    }
  }

  // Utility Methods
  isStorageAvailable(): boolean {
    try {
      const test = '__storage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }

  clearAllData(): void {
    try {
      Object.values(this.STORAGE_KEYS).forEach(key => {
        localStorage.removeItem(key);
      });
    } catch (error) {
    }
  }

  // Get subscription run IDs only
  getSubscriptionRunIds(): string[] {
    return this.getSubscriptions().map(sub => sub.runId);
  }

  // Check if subscribed to a specific run
  isSubscribedToRun(runId: string): boolean {
    return this.getSubscriptions().some(sub => sub.runId === runId);
  }

  // Get subscriptions count
  getSubscriptionsCount(): number {
    return this.getSubscriptions().length;
  }
}

export default NotificationPersistenceService; 